import React, { useState, useRef, useEffect } from 'react';
import { banks, searchBanks, getBankTier, getTierInfo } from '../../data/banks';
import './BankSelector.css';

const BankSelector = ({ value, onChange, placeholder = "Select or type bank name..." }) => {
  const [searchQuery, setSearchQuery] = useState(value || '');
  const [filteredBanks, setFilteredBanks] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomOption, setShowCustomOption] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (value) {
      setSearchQuery(value);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.trim() === '') {
      setFilteredBanks([]);
      setShowCustomOption(false);
      setIsOpen(false);
      onChange({ name: '', tier: null });
      return;
    }

    const filtered = searchBanks(query);
    setFilteredBanks(filtered);
    setShowCustomOption(filtered.length === 0 || !filtered.some(b => b.name.toLowerCase() === query.toLowerCase()));
    setIsOpen(true);
  };

  const handleBankSelect = (bank) => {
    setSearchQuery(bank.name);
    setIsOpen(false);
    onChange({ name: bank.name, tier: bank.tier });
  };

  const handleCustomBank = () => {
    const customName = searchQuery.trim();
    if (customName) {
      setIsOpen(false);
      onChange({ name: customName, tier: null });
    }
  };

  const handleInputFocus = () => {
    if (searchQuery.trim()) {
      const filtered = searchBanks(searchQuery);
      setFilteredBanks(filtered);
      setShowCustomOption(filtered.length === 0 || !filtered.some(b => b.name.toLowerCase() === searchQuery.toLowerCase()));
    } else {
      setFilteredBanks(banks.slice(0, 20)); // Show first 20 banks
    }
    setIsOpen(true);
  };

  const selectedTier = getBankTier(searchQuery);
  const tierInfo = selectedTier ? getTierInfo(selectedTier) : null;

  return (
    <div className="bank-selector" ref={containerRef}>
      <div className="bank-selector-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="bank-selector-input"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
        />
        {tierInfo && (
          <span 
            className="bank-tier-badge"
            style={{
              backgroundColor: tierInfo.bgColor,
              color: tierInfo.color,
            }}
            title={tierInfo.description}
          >
            {tierInfo.label}
          </span>
        )}
      </div>
      
      {isOpen && (filteredBanks.length > 0 || showCustomOption) && (
        <div className="bank-selector-dropdown">
          {filteredBanks.map((bank, index) => {
            const bankTierInfo = getTierInfo(bank.tier);
            return (
              <div
                key={index}
                className="bank-selector-option"
                onClick={() => handleBankSelect(bank)}
              >
                <span className="bank-option-name">{bank.name}</span>
                <span
                  className="bank-option-tier"
                  style={{
                    backgroundColor: bankTierInfo.bgColor,
                    color: bankTierInfo.color,
                  }}
                >
                  {bankTierInfo.label}
                </span>
              </div>
            );
          })}
          {showCustomOption && (
            <div
              className="bank-selector-option bank-selector-custom"
              onClick={handleCustomBank}
            >
              <span>Use "{searchQuery}" (Custom)</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BankSelector;

